import { useState, useEffect, useCallback } from 'react'
import { pathToViewState } from '../utils/urlRouter'

/**
 * useRouter — History API integration hook.
 *
 * Reads current URL on mount, listens for popstate (back/forward),
 * and provides navigate/replace for programmatic navigation.
 *
 * @returns {{ view: string, params: object, navigate: (path: string) => void, replace: (path: string) => void }}
 */
export function useRouter() {
  const [state, setState] = useState(() =>
    pathToViewState(window.location.pathname, window.location.search)
  )

  // Listen for browser back/forward
  useEffect(() => {
    function handlePopState() {
      setState(pathToViewState(window.location.pathname, window.location.search))
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Push a new history entry
  const navigate = useCallback((path) => {
    window.history.pushState(null, '', path)
    setState(pathToViewState(
      new URL(path, window.location.origin).pathname,
      new URL(path, window.location.origin).search
    ))
  }, [])

  // Replace current history entry (for redirects, feature gates)
  const replace = useCallback((path) => {
    window.history.replaceState(null, '', path)
    setState(pathToViewState(
      new URL(path, window.location.origin).pathname,
      new URL(path, window.location.origin).search
    ))
  }, [])

  return {
    view: state.view,
    params: state,
    navigate,
    replace,
  }
}
